/**
 * useNavigationPermissions Hook
 *
 * Filters navigation items based on feature flags and user roles.
 * Items with requiresFeature property are only shown if that feature is enabled.
 * Items with requiresAdminOrOwner property are only shown to admin/owner users.
 */

import { useMemo } from 'react';
import { useFeatureFlags } from '../../../context/FeatureFlagContext';
import { useAuth } from '../../../context/AuthContext';

export const useNavigationPermissions = navigationItems => {
  let isFeatureEnabled;
  try {
    const featureFlags = useFeatureFlags();
    isFeatureEnabled = featureFlags.isFeatureEnabled;
  } catch (error) {
    // Error getting feature flags - default to disabled
    isFeatureEnabled = () => false;
  }

  // Get user role check function
  let isAdminOrOwner;
  try {
    const auth = useAuth();
    isAdminOrOwner = auth.isAdminOrOwner;
  } catch (error) {
    // Error getting auth context - default to false
    isAdminOrOwner = () => false;
  }

  // Recursively filter navigation items and their children based on feature flags and roles
  const filterItems = useMemo(() => {
    if (!navigationItems || !Array.isArray(navigationItems)) {
      return [];
    }

    const filtered = navigationItems
      .map(item => {
        // If item has requiresFeature, check if feature is enabled
        if (item.requiresFeature) {
          const isEnabled = isFeatureEnabled(item.requiresFeature);
          if (!isEnabled) {
            return null; // Filter out this item
          }
        }

        // If item has requiresAdminOrOwner, check if user is admin or owner
        if (item.requiresAdminOrOwner) {
          const hasPermission = isAdminOrOwner();
          if (!hasPermission) {
            return null; // Filter out this item
          }
        }

        // Process children recursively if they exist
        if (item.children && Array.isArray(item.children)) {
          const filteredChildren = item.children
            .map(child => {
              // Check if child has requiresFeature
              if (child.requiresFeature) {
                const isChildEnabled = isFeatureEnabled(child.requiresFeature);
                if (!isChildEnabled) {
                  return null; // Filter out this child
                }
              }

              // Check if child has requiresAdminOrOwner
              if (child.requiresAdminOrOwner) {
                const hasPermission = isAdminOrOwner();
                if (!hasPermission) {
                  return null; // Filter out this child
                }
              }

              // Recursively process nested children
              if (child.children && Array.isArray(child.children)) {
                const filteredNestedChildren = child.children
                  .map(nestedChild => {
                    if (nestedChild.requiresFeature) {
                      return isFeatureEnabled(nestedChild.requiresFeature)
                        ? nestedChild
                        : null;
                    }
                    if (nestedChild.requiresAdminOrOwner) {
                      return isAdminOrOwner() ? nestedChild : null;
                    }
                    return nestedChild;
                  })
                  .filter(Boolean);
                return { ...child, children: filteredNestedChildren };
              }

              return child;
            })
            .filter(Boolean);

          // Always show parent if it has no requiresFeature (like Platform)
          // Even if all children are filtered out, show the parent
          // If parent has requiresFeature and is enabled, show it with filtered children
          return {
            ...item,
            children: filteredChildren, // May be empty array, but parent still shows
          };
        }

        // Item has no children, return as-is if it passed the feature check
        return item;
      })
      .filter(Boolean); // Remove null items

    return filtered;
  }, [navigationItems, isFeatureEnabled, isAdminOrOwner]);

  return {
    visibleItems: filterItems,
    loading: false,
  };
};

export default useNavigationPermissions;
